// ----------------------------------------------------------------------------
// Product Form Extension: Sticky Add to Cart
// ----------------------------------------------------------------------------
// Sentinel IntersectionObserver toggles the fixed bar; variant / price sync separately.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';
import { formatMoney } from '@shopify/theme-currency';

// Styles
import './_product-form--sticky.scss';
var STICKY_BAR = '[data-sticky-atc-bar]';
var VARIANT_DATA = '[data-sticky-atc-variant-data]';
var PRIMARY_ATC = '[data-sticky-atc-primary] iam-add-to-cart-button';
function getCurrencyFormat() {
  return window.theme && window.theme.settingCurrencyFormat;
}
function formatCents(cents) {
  var n = Number(cents);
  if (Number.isNaN(n)) return '';
  return formatMoney(n, getCurrencyFormat());
}
function parseVariantMeta(root) {
  var script = root.querySelector(VARIANT_DATA);
  if (!(script !== null && script !== void 0 && script.textContent)) return {};
  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.warn('sticky-atc: invalid variant JSON', e);
    return {};
  }
}
function isStickyAddToCartSource(addToCartSource) {
  var _addToCartSource$clos;
  return Boolean(addToCartSource === null || addToCartSource === void 0 || (_addToCartSource$clos = addToCartSource.closest) === null || _addToCartSource$clos === void 0 ? void 0 : _addToCartSource$clos.call(addToCartSource, STICKY_BAR));
}
function getTextContent(node) {
  if (!node) return '';
  return node.textContent.replace(/\s+/g, ' ').trim();
}
function getPriceMarkupRoot(priceMarkupOrElement) {
  if (!priceMarkupOrElement) return null;
  if (typeof priceMarkupOrElement !== 'string') {
    return priceMarkupOrElement;
  }
  var template = document.createElement('template');
  template.innerHTML = priceMarkupOrElement;
  return template.content;
}
function getPriceDisplayState(priceMarkupOrElement) {
  var root = getPriceMarkupRoot(priceMarkupOrElement);
  if (!root) return null;
  var priceText = getTextContent(root.querySelector('.price__original'));
  var compareText = getTextContent(root.querySelector('.price__sale'));
  return {
    priceText,
    compareText,
    onSale: Boolean(compareText)
  };
}
function clearStickyPriceState(root) {
  var priceEl = root.querySelector('[data-sticky-atc-price]');
  var compareWrap = root.querySelector('[data-sticky-atc-compare-wrap]');
  var compareEl = root.querySelector('[data-sticky-atc-compare-price]');
  var saleTag = root.querySelector('[data-sticky-atc-sale-tag]');
  if (priceEl) {
    priceEl.textContent = '';
  }
  if (compareWrap) {
    compareWrap.hidden = true;
    compareWrap.classList.add('visually-hidden');
  }
  if (compareEl) {
    compareEl.textContent = '';
  }
  if (saleTag) {
    saleTag.hidden = true;
  }
}
function syncStickyPriceState(root, variant, priceMarkupOrElement) {
  var priceEl = root.querySelector('[data-sticky-atc-price]');
  var compareWrap = root.querySelector('[data-sticky-atc-compare-wrap]');
  var compareEl = root.querySelector('[data-sticky-atc-compare-price]');
  var saleTag = root.querySelector('[data-sticky-atc-sale-tag]');
  if (!variant) {
    clearStickyPriceState(root);
    return;
  }
  var priceState = getPriceDisplayState(priceMarkupOrElement);
  var priceText = (priceState === null || priceState === void 0 ? void 0 : priceState.priceText) || formatCents(variant.price);
  var compareText = (priceState === null || priceState === void 0 ? void 0 : priceState.compareText) || '';
  var onSale = Boolean(compareText);
  if (priceEl) {
    priceEl.textContent = priceText;
  }
  if (compareWrap && compareEl) {
    compareEl.textContent = compareText;
    compareWrap.hidden = !onSale;
    compareWrap.classList.toggle('visually-hidden', !onSale);
  }
  if (saleTag) {
    saleTag.hidden = !onSale;
  }
}

/**
 * @param {HTMLElement} root
 * @param {object|null} variant
 * @param {object} variantMetaById
 * @param {string[]} optionNames
 * @param {boolean} hideSingleSizeOption
 */
function updateStickyContent(root, variant, variantMetaById, optionNames, hideSingleSizeOption) {
  var img = root.querySelector('[data-sticky-atc-image]');
  var capacityEl = root.querySelector('[data-sticky-atc-capacity]');
  var optionsEl = root.querySelector('[data-sticky-atc-options]');
  var oosMessage = root.querySelector('[data-sticky-atc-oos-message]');
  var notifyBtn = root.querySelector('[data-sticky-atc-notify]');
  var atcWrap = root.querySelector('[data-sticky-atc-atc-wrap]');
  if (!variant) {
    root.classList.remove('sticky-atc-root--oos');
    if (atcWrap) atcWrap.hidden = false;
    if (notifyBtn) notifyBtn.hidden = true;
    if (oosMessage) oosMessage.hidden = true;
    clearStickyPriceState(root);
    return;
  }
  var meta = variantMetaById[String(variant.id)] || {};
  var available = typeof meta.available === 'boolean' ? meta.available : variant.available;
  if (img) {
    var _variant$featured_ima;
    var url = meta.featured_image || ((_variant$featured_ima = variant.featured_image) === null || _variant$featured_ima === void 0 ? void 0 : _variant$featured_ima.src) || null;
    if (url) {
      var _variant$featured_ima2;
      img.src = url;
      img.hidden = false;
      if ((_variant$featured_ima2 = variant.featured_image) !== null && _variant$featured_ima2 !== void 0 && _variant$featured_ima2.alt) {
        img.alt = variant.featured_image.alt;
      }
    } else {
      img.src = 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\'/%3E';
      img.hidden = true;
    }
  }
  if (capacityEl) {
    var cap = meta.capacity;
    if (cap) {
      capacityEl.textContent = "\u2014 ".concat(cap);
      capacityEl.classList.remove('visually-hidden');
    } else {
      capacityEl.textContent = '';
      capacityEl.classList.add('visually-hidden');
    }
  }
  if (optionsEl && optionNames.length) {
    optionsEl.innerHTML = '';
    optionNames.forEach((name, index) => {
      var normalizedName = String(name).trim().toLowerCase();
      if (hideSingleSizeOption && normalizedName.includes('size')) return;
      var val = variant["option".concat(index + 1)];
      if (!val) return;
      var span = document.createElement('span');
      span.className = 'sticky-atc__option';
      span.dataset.stickyAtcOption = normalizedName.replace(/\s+/g, '-');
      span.textContent = "".concat(normalizedName, ": ").concat(String(val).toLowerCase());
      optionsEl.appendChild(span);
    });
    var hasVisibleOptions = optionsEl.children.length > 0;
    optionsEl.hidden = !hasVisibleOptions;
    optionsEl.classList.toggle('visually-hidden', !hasVisibleOptions);
  }
  syncStickyPriceState(root, variant, null);
  var soldOut = !available;
  root.classList.toggle('sticky-atc-root--oos', soldOut);
  if (atcWrap) atcWrap.hidden = false;
  if (notifyBtn) notifyBtn.hidden = true;
  if (oosMessage) oosMessage.hidden = !soldOut;
}
function setBarAriaHidden(root, hidden) {
  var bar = root.querySelector('[data-sticky-atc-bar]');
  if (bar) {
    bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }
}
function focusStickyBarInteractive(root) {
  var bar = root.querySelector(STICKY_BAR);
  if (!bar) return;
  var notify = root.querySelector('[data-sticky-atc-notify]');
  if (notify && !notify.hidden) {
    notify.focus({
      preventScroll: true
    });
    return;
  }
  var addBtn = bar.querySelector('iam-add-to-cart-button button');
  if (addBtn && !addBtn.disabled) {
    addBtn.focus({
      preventScroll: true
    });
  }
}
class StickyAtc {
  constructor(iamProductForm) {
    var _this$iamForm$querySe;
    this.iamForm = iamProductForm;
    this.root = iamProductForm.querySelector('#productStickyContainer');
    if (!this.root) return;
    this.variantMetaById = parseVariantMeta(this.root);
    this.optionNames = [];
    this.hideSingleSizeOption = false;
    try {
      var raw = this.root.dataset.stickyAtcOptionNames;
      if (raw) this.optionNames = JSON.parse(raw);
    } catch (e) {
      console.warn('sticky-atc: option names JSON', e);
    }
    this.hideSingleSizeOption = this.root.dataset.stickyAtcHideSingleSizeOption === 'true';
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onPriceUpdated = this.onPriceUpdated.bind(this);
    this.onPrimaryAtcIntersect = this.onPrimaryAtcIntersect.bind(this);
    this.scheduleViewportSync = this.scheduleViewportSync.bind(this);
    this.onStickyButtonText = this.onStickyButtonText.bind(this);
    this._wasPinned = false;
    this._isPrimaryAtcPastTop = false;
    this._isPastMainContent = false;
    this._viewportSyncScheduled = false;
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange, {
      context: this.iamForm,
      source: "product-form--sticky.js"
    });
    iamotaEventHandler.subscribe('productForm:price:updated', this.onPriceUpdated, {
      context: this.iamForm,
      source: "product-form--sticky.js"
    });
    iamotaEventHandler.subscribe('productForm:addToCartButton:buttonText', this.onStickyButtonText, {
      priority: 5,
      source: "product-form--sticky.js"
    });
    this.initPinnedTracking();
    this.onVariantChange({
      variant: this.iamForm.getCurrentVariant()
    });
    this.onPriceUpdated({
      priceHTML: ((_this$iamForm$querySe = this.iamForm.querySelector('iam-product-price')) === null || _this$iamForm$querySe === void 0 ? void 0 : _this$iamForm$querySe.innerHTML) || ''
    });
  }
  onStickyButtonText(buttonText, additionalData) {
    if (!isStickyAddToCartSource(additionalData.addToCartSource)) return undefined;
    var plain = translate('Add to Cart', 'stickyAtc.add_to_cart');
    var lower = String(buttonText).toLowerCase();
    if (lower.includes('adding to cart')) return buttonText;
    if (lower.includes('item added')) return buttonText;
    if (lower.includes('loading')) return buttonText;
    if (lower.includes('unable to add') || lower.includes('unavailable')) return buttonText;
    if (lower.includes('sold out')) return buttonText;
    if (lower.includes('add to cart')) return plain;
    return undefined;
  }
  onVariantChange(_ref) {
    var variant = _ref.variant;
    updateStickyContent(this.root, variant, this.variantMetaById, this.optionNames, this.hideSingleSizeOption);
  }
  onPriceUpdated(_ref2) {
    var priceHTML = _ref2.priceHTML;
    syncStickyPriceState(this.root, this.iamForm.getCurrentVariant(), priceHTML);
  }

  /**
   * Pin the fixed bar when the in-flow primary Add to Cart leaves the viewport upward.
   * The bar’s own button is `position:fixed`, so it must not be the observed target.
   * @param {IntersectionObserverEntry[]} entries
   */
  onPrimaryAtcIntersect(entries) {
    var entry = entries[0];
    if (!entry) return;
    var rect = entry.boundingClientRect;
    this._isPrimaryAtcPastTop = !entry.isIntersecting && rect.bottom < 0;
    this.syncPinnedState();
  }
  isPastMainContent() {
    if (!this.mainElement) {
      return false;
    }
    var rect = this.mainElement.getBoundingClientRect();
    return rect.bottom <= window.innerHeight;
  }
  syncViewportPosition() {
    this._isPastMainContent = this.isPastMainContent();
    this.syncPinnedState();
  }
  scheduleViewportSync() {
    if (this._viewportSyncScheduled) {
      return;
    }
    this._viewportSyncScheduled = true;
    requestAnimationFrame(() => {
      this._viewportSyncScheduled = false;
      this.syncViewportPosition();
    });
  }
  syncPinnedState() {
    var pinned = this._isPrimaryAtcPastTop && !this._isPastMainContent;
    var wasPinned = this._wasPinned;
    this._wasPinned = pinned;
    this.root.classList.toggle('sticky-atc-root--pinned', pinned);
    setBarAriaHidden(this.root, !pinned);
    if (pinned && !wasPinned) {
      requestAnimationFrame(() => focusStickyBarInteractive(this.root));
    }
  }
  initPinnedTracking() {
    this.primaryAtc = this.root.querySelector(PRIMARY_ATC);
    if (!this.primaryAtc) return;
    this.mainElement = document.querySelector('main');
    this._isPastMainContent = this.isPastMainContent();
    this.observer = new IntersectionObserver(this.onPrimaryAtcIntersect, {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.01, 0.1, 1]
    });
    this.observer.observe(this.primaryAtc);
    window.addEventListener('scroll', this.scheduleViewportSync, {
      passive: true
    });
    window.addEventListener('resize', this.scheduleViewportSync);
    this.syncPinnedState();
  }
}
iamotaEventHandler.subscribe('productForm:init', _ref3 => {
  var iamForm = _ref3.iamForm;
  new StickyAtc(iamForm);
}, {
  source: "product-form--sticky.js"
});